import React from 'react'

import { TableCell } from '@components/common/TableCell'
import { TableProductsList } from '@components/common/TableProductsList'
import type { TableHeaders } from '@fourthwall/components'
import { usePagination } from '@fourthwall/components'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { reverse, routing } from '@utils/routing'

import type { GiveawaysItem } from '../../../../Contributions/pages/Orders/pages/GiveawaysList'
import { useGiveawayContributionsQuery } from '../../../../Contributions/pages/Orders/pages/GiveawaysList'
import type { AppsTwitchViewProps } from '../AppsTwitch.types'

export const usePurchasedGifts = () => {
  const [currentPage, setCurrentPage] = usePagination()
  const { giveawayContributionsQuery } = useGiveawayContributionsQuery(
    [{ page: currentPage - 1, size: 4 }],
    { keepPreviousData: true },
  )

  const data: NonNullable<AppsTwitchViewProps['giveaways']>['table']['data'] =
    giveawayContributionsQuery.data?.result || []

  const headers: TableHeaders<GiveawaysItem> = [
    {
      id: 'date',
      label: 'Date',
      render: ({ createdAt }) => <TimezonedDateString date={createdAt} format="MM/DD/YYYY" />,
    },
    {
      id: 'product',
      label: 'Product',
      render: ({ productImageUrl }) => (
        <TableProductsList image={productImageUrl} offers={[]} offersCancelled={[]} quantity={1} />
      ),
    },
    {
      id: 'purchasedBy',
      label: 'Purchased by',
      render: ({ supporter }) => (
        <TableCell mainContent={supporter.username || supporter.email} $ellipsis />
      ),
    },
    {
      id: 'claimed',
      label: 'Claimed',
      render: ({ claimedGifts, numberOfGifts }) => `${claimedGifts}/${numberOfGifts}`,
    },
  ]

  const table: NonNullable<AppsTwitchViewProps['giveaways']>['table'] = {
    data,
    headers,
    // @ts-ignore
    getRowLink: ({ giveawayId }: { giveawayId: string }) => {
      return reverse(routing.contributions.orders.giveaways.giveaway, { giveawayId })
    },
  }

  const getPagination = (
    length: number,
  ): NonNullable<AppsTwitchViewProps['giveaways']>['pagination'] => {
    if (!length) return

    return {
      pageCount: giveawayContributionsQuery.data?.page.totalPages || 1,
      currentPage,
      onChange: setCurrentPage,
    }
  }

  const getGiveaways = (): AppsTwitchViewProps['giveaways'] => {
    if (giveawayContributionsQuery.isLoading) return undefined

    return {
      table,
      pagination: getPagination(table.data.length),
    }
  }

  return {
    purchasedGifts: {
      giveaways: getGiveaways(),
    },
  }
}
